'use client'

import { Button } from '@/components/ui/button'
import { Divider } from '@/components/ui/divider'
import { useIsMobileClient } from '@/hooks/common/use-device-type'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn } from '@/lib/utils'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'
import { EmployeeIcon } from '../icons/employee'
import { ProductionCapacityIcon } from '../icons/production-capacity'
import { factories } from './supplier-data'

interface FactoryCardProps {
  i18nKey: string
  slug: string
  image: string
  stats?: {
    employees: string
    productsKey?: string
  }
  link?: string
}

function FactoryCard({ i18nKey, image, stats, link, slug }: FactoryCardProps) {
  const t = useTranslations('landing_supplier')

  return (
    <Link
      href={link || `/supplier/${slug}`}
      className='group flex flex-col rounded-lg bg-white'
    >
      <div className='relative mb-4 h-60 overflow-hidden rounded-lg'>
        <Image
          src={ImageHelper.getImageUrlCdn(image)}
          alt={t(`${i18nKey}.name`)}
          fill
          className={'object-cover transition-transform group-hover:scale-105'}
        />
      </div>
      <div className='flex flex-1 flex-col justify-between px-5 pb-5 md:px-6'>
        <div>
          <h3 className='mb-2 font-criteria text-xl font-medium text-neutral-900 md:text-2xl'>
            {t(`${i18nKey}.name`)}
          </h3>
          <p className='line-clamp-2 text-sm text-neutral-500'>
            {t.raw(`${i18nKey}.short_description`)}
          </p>
        </div>

        {stats ? (
          <div>
            <Divider />
            <div className='flex items-center justify-between gap-6'>
              <div>
                {stats?.productsKey ? (
                  <div className='flex items-start gap-2'>
                    <div className='mt-1'>
                      <ProductionCapacityIcon />
                    </div>
                    <div>
                      <div className='text-sm text-neutral-900'>
                        {t('factories.production_capacity')}
                      </div>
                      <div className='text-base font-bold text-primary'>
                        {t(stats?.productsKey)}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className='flex items-start gap-2'>
                <EmployeeIcon />
                <div>
                  <div className='text-sm text-neutral-900'>
                    {t('factories.employees')}
                  </div>
                  <div className='text-base font-bold text-primary'>
                    {stats?.employees}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </Link>
  )
}

export function FactoriesSection() {
  const t = useTranslations('landing_supplier.factories')
  const [isShowAll, setIsShowAll] = useState(false)
  const isMobile = useIsMobileClient()
  const maxFactories = isMobile ? 4 : 6
  const displayedFactories = isShowAll
    ? factories
    : factories.slice(0, maxFactories)

  return (
    <section className='bg-neutral-100 px-5 py-10 md:mt-20 md:px-16'>
      <div className='2xl:mx-auto 2xl:max-w-7xl'>
        <h2 className='mb-2 text-center text-2xl font-bold md:mb-4 md:text-3xl'>
          {t('title')}
        </h2>
        <p className='mx-auto mb-6 text-center text-gray-700 dark:text-gray-300'>
          {t('subtitle')}
        </p>
        <div className='grid gap-6 md:grid-cols-2 lg:grid-cols-3'>
          {displayedFactories.map((factory, index) => (
            <FactoryCard key={index} {...factory} />
          ))}
        </div>

        {factories.length > maxFactories ? (
          <div
            className={cn(
              'mt-4 text-center md:mt-12',
              isShowAll ? 'sticky bottom-5 left-0 right-0 z-50' : ''
            )}
          >
            <Button onClick={() => setIsShowAll(!isShowAll)} variant='black'>
              {isShowAll ? t('collapse') : t('see_more')}
            </Button>
          </div>
        ) : null}
      </div>
    </section>
  )
}
